
import React, { useState, useMemo, useEffect } from 'react';
import { List, Camera, Menu, X, Moon, Sun, Settings, LogOut, BarChart3, LayoutDashboard, BrickWall, ArrowLeftRight, WifiOff } from 'lucide-react';
import { auth } from '../services/firebase';
import { BrandLogo } from './BrandLogo';

interface LayoutProps {
  children: React.ReactNode;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  projectName?: string;
  projectLogo?: string;
  onLogout: () => void;
  onSwitchProject: () => void;
}

export const Layout: React.FC<LayoutProps> = ({ 
  children, activeTab, setActiveTab, isDarkMode, toggleDarkMode, 
  projectName, projectLogo, onLogout, onSwitchProject
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const user = auth.currentUser;
  const userName = user?.displayName || "Proprietário";
  const userInitials = useMemo(() => {
    return userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  }, [userName]);

  const tabs = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Painel', tooltip: 'Visão Geral da Obra' },
    { id: 'expenses', icon: List, label: 'Extrato', tooltip: 'Controle Financeiro' },
    { id: 'photos', icon: Camera, label: 'Diário', tooltip: 'Histórico de Fotos' },
    { id: 'stats', icon: BarChart3, label: 'Relatórios', tooltip: 'Análise de Custos' },
    { id: 'settings', icon: Settings, label: 'Ajustes', tooltip: 'Configurações do Projeto' },
  ];

  const handleLogoutConfirm = () => {
    if (window.confirm("Deseja realmente sair da sua conta?")) {
      onLogout();
    }
  };

  const NavItem: React.FC<{ tab: any; mobile?: boolean }> = ({ tab, mobile = false }) => {
    const isActive = activeTab === tab.id;
    return (
      <button
        id={`tour-tab-${tab.id}`}
        onClick={() => {
          setActiveTab(tab.id);
          if (mobile) setIsMobileMenuOpen(false);
        }}
        title={tab.tooltip}
        className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 group ${
          isActive 
            ? 'bg-emerald-800 text-white shadow-xl shadow-emerald-800/20 scale-[1.02]' 
            : 'text-slate-500 hover:bg-emerald-50 dark:hover:bg-slate-800 hover:text-emerald-700 dark:hover:text-emerald-400'
        }`}
      >
        <tab.icon size={20} className={isActive ? 'text-white' : 'text-slate-400 group-hover:text-emerald-600'} />
        <span className={`text-sm font-black uppercase tracking-widest ${mobile ? 'text-base' : ''}`}>{tab.label}</span>
      </button>
    );
  };

  return (
    <div className={`min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex transition-colors duration-300`}>
      
      {/* Offline Banner */}
      {isOffline && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-rose-500 text-white text-center py-1 px-4 text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg">
          <WifiOff size={12} /> Você está offline. As alterações serão salvas quando a conexão retornar.
        </div>
      )}
      
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col w-72 bg-white dark:bg-slate-900 border-r dark:border-slate-800 h-screen sticky top-0 z-50 ${isOffline ? 'mt-6' : ''}`}>
        <div className="p-8 flex items-center gap-4 border-b dark:border-slate-800 mb-8">
          <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl flex items-center justify-center shadow-sm overflow-hidden shrink-0">
            {projectLogo ? (
              <img src={projectLogo} className="w-full h-full object-cover" alt="Logo da Obra" />
            ) : (
              <BrandLogo variant="icon" size="sm" />
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-black uppercase text-emerald-800 dark:text-emerald-400 tracking-[0.2em] leading-none">Minha Obra</span>
            <span className="text-sm font-bold text-slate-400 truncate w-full" title={projectName}>{projectName || "Projeto"}</span>
          </div>
        </div>

        <nav className="flex-1 px-6 space-y-3">
          <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.3em] px-4 mb-6 opacity-60">Menu Principal</p>
          {tabs.map((tab) => <NavItem key={tab.id} tab={tab} />)}
        </nav>

        <div className="p-6 border-t dark:border-slate-800 space-y-3">
           <button 
             onClick={onSwitchProject}
             className="w-full flex items-center justify-between px-5 py-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:text-emerald-600 transition-all group"
           >
             <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300 group-hover:text-emerald-600">
               <ArrowLeftRight size={20} />
               <span className="text-xs font-black uppercase tracking-widest">Trocar Obra</span>
             </div>
           </button>

          <button 
            onClick={toggleDarkMode}
            title="Alternar Tema Escuro/Claro"
            className="w-full flex items-center justify-between px-5 py-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 transition-all"
          >
            <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
              {isDarkMode ? <Moon size={20} /> : <Sun size={20} />}
              <span className="text-xs font-black uppercase tracking-widest">Tema</span>
            </div>
          </button>
          
          <button 
            type="button"
            onClick={handleLogoutConfirm}
            title="Encerrar Sessão"
            className="w-full flex items-center gap-3 px-5 py-4 rounded-2xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors font-black text-xs uppercase tracking-widest"
          >
            <LogOut size={20} />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col min-w-0 pb-20 md:pb-0 ${isOffline ? 'mt-6' : ''}`}>
        {/* Top Header */}
        <header className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-b dark:border-slate-800 px-6 sm:px-10 py-5 flex justify-between items-center sticky top-0 z-40">
          <div className="flex items-center gap-4 md:hidden">
            <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl flex items-center justify-center overflow-hidden">
               {projectLogo ? (
                  <img src={projectLogo} className="w-full h-full object-cover" alt="Logo" />
                ) : (
                  <BrandLogo variant="icon" size="sm" />
                )}
            </div>
            <h1 className="text-sm font-black text-slate-900 dark:text-white tracking-tighter uppercase truncate max-w-[150px]">{projectName}</h1>
          </div>
          
          <div className="flex items-center gap-4 ml-auto">
            {/* User Greeting Area */}
            <div className="flex items-center gap-3 pl-4 border-l border-slate-200 dark:border-slate-700">
               <div className="text-right hidden sm:block">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-0.5">
                    Bem-vindo,
                  </p>
                  <p className="text-sm font-black text-slate-900 dark:text-white leading-none">{userName.split(' ')[0]}</p>
               </div>
               <div className="size-10 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 p-0.5 shadow-lg shadow-emerald-500/20 group cursor-pointer hover:scale-105 transition-transform">
                  <div className="w-full h-full rounded-full bg-white dark:bg-slate-900 flex items-center justify-center">
                     <span className="font-black text-xs text-transparent bg-clip-text bg-gradient-to-br from-emerald-600 to-emerald-800">
                        {userInitials}
                     </span>
                  </div>
               </div>
            </div>
            
            <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl text-slate-600 dark:text-slate-300">
              <Menu size={22} />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto w-full max-w-7xl mx-auto px-6 sm:px-10 py-10 no-scrollbar">
          {children}
        </main>
      </div>

      {/* MOBILE BOTTOM NAVIGATION */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 pb-safe pt-2 px-2 z-50 flex justify-around items-end shadow-[0_-5px_20px_rgba(0,0,0,0.05)]">
         {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button 
                key={tab.id}
                id={`tour-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center gap-1 p-2 pb-4 transition-all duration-300 ${isActive ? 'text-emerald-600 -translate-y-2' : 'text-slate-400'}`}
              >
                 <div className={`p-2 rounded-2xl transition-all ${isActive ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30' : 'bg-transparent'}`}>
                    <tab.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                 </div>
                 <span className={`text-[9px] font-black uppercase tracking-widest transition-opacity ${isActive ? 'opacity-100 text-emerald-700 dark:text-emerald-400' : 'opacity-0 h-0 overflow-hidden'}`}>
                    {tab.label}
                 </span>
              </button>
            )
         })}
      </div>

      {/* Mobile Settings Sidebar (Profile/Logout only) */}
      {isMobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[100]" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="fixed inset-y-0 right-0 w-80 bg-white dark:bg-slate-900 z-[110] p-10 flex flex-col animate-fadeIn">
            <div className="flex justify-between items-center mb-12">
              <h2 className="font-black text-xl uppercase tracking-widest">Conta</h2>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 bg-slate-100 dark:bg-slate-800 rounded-full"><X size={24}/></button>
            </div>
            
            <div className="flex-1 space-y-6">
                <div className="p-6 bg-slate-50 dark:bg-slate-800 rounded-3xl flex flex-col items-center text-center">
                    <div className="size-20 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 p-0.5 shadow-lg shadow-emerald-500/20 mb-4">
                      <div className="w-full h-full rounded-full bg-white dark:bg-slate-900 flex items-center justify-center">
                        <span className="font-black text-2xl text-transparent bg-clip-text bg-gradient-to-br from-emerald-600 to-emerald-800">
                            {userInitials}
                        </span>
                      </div>
                    </div>
                    <p className="font-black text-slate-900 dark:text-white text-lg">{userName}</p>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Proprietário</p>
                </div>

                <div className="space-y-3">
                  <button 
                    onClick={toggleDarkMode}
                    className="w-full flex items-center justify-between px-5 py-4 rounded-2xl bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 transition-all"
                  >
                    <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                      {isDarkMode ? <Moon size={20} /> : <Sun size={20} />}
                      <span className="text-xs font-black uppercase tracking-widest">Alterar Tema</span>
                    </div>
                  </button>
                </div>
            </div>

            <div className="pt-8 border-t dark:border-slate-800 space-y-3">
               <button type="button" onClick={() => { setIsMobileMenuOpen(false); onSwitchProject(); }} className="w-full flex items-center gap-4 p-5 text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-2xl font-black text-xs uppercase tracking-widest">
                  <ArrowLeftRight size={20} /> Trocar Obra
               </button>
               <button type="button" onClick={handleLogoutConfirm} className="w-full flex items-center gap-4 p-5 text-red-500 bg-red-50 dark:bg-red-900/10 rounded-2xl font-black text-xs uppercase tracking-widest">
                  <LogOut size={20} /> Sair
               </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
